import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; jose: any }
    | { kind: 'error'; message: string };

type Algo = 'HS256' | 'HS384' | 'HS512' | 'RS256' | 'ES256';

const DEFAULT_PAYLOAD = `{
  "sub": "1234567890",
  "name": "Jane Doe",
  "iat": ${Math.floor(Date.now() / 1000)}
}`;

interface State {
    algorithm: Algo;
    payload: string;
    secret: string;
    privateKey: string;
    output: string;
    error: string | null;
    lib: LibState;
    busy: boolean;
    copied: boolean;
}

export default class JwtEncoderComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        algorithm: 'HS256',
        payload: DEFAULT_PAYLOAD,
        secret: 'super-secret-key-replace-me',
        privateKey: '',
        output: '',
        error: null,
        lib: { kind: 'idle' },
        busy: false,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureJose(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.jose;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const jose = await import('jose');
            this.setState({ lib: { kind: 'ready', jose } });
            return jose;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load jose' } });
            return null;
        }
    }

    sign = async () => {
        this.setState({ busy: true });
        const jose = await this.ensureJose();
        if (!jose) { this.setState({ busy: false }); return; }
        try {
            const payload = JSON.parse(this.state.payload);
            let signKey: any;
            if (this.state.algorithm.startsWith('HS')) {
                signKey = new TextEncoder().encode(this.state.secret);
            } else {
                signKey = await jose.importPKCS8(this.state.privateKey.trim(), this.state.algorithm);
            }
            const jwt = await new jose.SignJWT(payload)
                .setProtectedHeader({ alg: this.state.algorithm })
                .sign(signKey);
            this.setState({ output: jwt, error: null, busy: false, copied: false });
        } catch (e: any) {
            this.setState({ error: e.message || 'Sign failed', output: '', busy: false });
        }
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { algorithm, payload, secret, privateKey, output, error, lib, busy, copied } = this.state;
        const isHmac = algorithm.startsWith('HS');

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Header</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="jwt-algo">Algorithm</label>
                        <select id="jwt-algo" value={algorithm}
                            onChange={e => this.setState({ algorithm: e.target.value as Algo })}>
                            <option value="HS256">HS256 — HMAC SHA-256</option>
                            <option value="HS384">HS384 — HMAC SHA-384</option>
                            <option value="HS512">HS512 — HMAC SHA-512</option>
                            <option value="RS256">RS256 — RSA SHA-256 (PKCS#8 PEM)</option>
                            <option value="ES256">ES256 — ECDSA P-256 (PKCS#8 PEM)</option>
                        </select>
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Payload (JSON)</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="jwt-payload">Claims</label>
                        <textarea id="jwt-payload" rows={10} value={payload} spellCheck={false}
                            onChange={e => this.setState({ payload: e.target.value })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>{isHmac ? 'Secret' : 'Private key'}</span></div>
                    {isHmac ? (
                        <div className={genStyles.field}>
                            <label htmlFor="jwt-secret">Secret</label>
                            <input id="jwt-secret" type="text" value={secret} spellCheck={false}
                                onChange={e => this.setState({ secret: e.target.value })} />
                        </div>
                    ) : (
                        <div className={genStyles.field}>
                            <label htmlFor="jwt-pkey">Private key (PKCS#8 PEM)</label>
                            <textarea id="jwt-pkey" rows={8} value={privateKey} spellCheck={false}
                                placeholder={"-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----"}
                                onChange={e => this.setState({ privateKey: e.target.value })} />
                        </div>
                    )}
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.sign}
                        disabled={busy || lib.kind === 'loading'} style={{ alignSelf: 'flex-start' }}>
                        {busy ? 'Signing…' : lib.kind === 'loading' ? 'Loading jose…' : '⚙ Sign JWT'}
                    </button>
                </div>
            </>
        );

        const errMsg = error || (lib.kind === 'error' ? (lib as any).message : null);
        return (
            <GeneratorLayout
                form={form}
                previewLabel="Signed JWT"
                previewMeta={output ? `${output.length} chars` : undefined}
                preview={<pre className={genStyles.previewArea} aria-label="Signed JWT">{output || (errMsg ? `Error: ${errMsg}` : 'Token will appear here once you click Sign JWT.')}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy JWT'}
                    </button>
                }
            />
        );
    }
}
